import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  ArrowRight, ArrowLeft, Check, Lock, X, Upload, Plus,
  Megaphone, TrendingUp, Headphones, DollarSign, Users, Home, Activity,
  Bot, Plug, BookOpen, History as HistoryIcon, GitBranch,
  Target, Share2, PenTool, BarChart3, Kanban, Mail, LineChart,
  Ticket, ShieldAlert, Heart, Wallet, PieChart, FileText, ClipboardList,
  UserPlus, Smile,
  Paperclip, Mic, Send, Zap,
} from 'lucide-react';
import V4OnboardedHome from './V4OnboardedHome';
import { trackEvent, trackPageView } from '../../lib/analytics';

/* ─── Onboarding Flow — /v4/onboarding ───────────────────────────────────
   Single-page interactive onboarding: 5 screens → dashboard fade →
   post-dashboard setup modal. Pure mock; matches Implify V4 design system.
   ────────────────────────────────────────────────────────────────────── */

/* ─── Shared tokens ─── */
const RAIL_STEPS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'how-you-work', label: 'How You Work' },
  { id: 'action', label: 'Get Started' },
];

/* HOW YOU WORK — Step 2 progressive Q&A configuration.
 *
 * Q1: how-you-work tier (founder / dept-lead / team-member)
 * Q2: focus — 6 options per Q1 tier
 * Q3: only when Q1 ∈ {dept-lead, team-member} AND Q2 ∈ {Stay organised, Organise my work}
 * → which team are you on? (sales/marketing/support/people/finance/Other)
 *
 * Each Q2 option carries the routing it produces:
 *   - agent  : agent id to default-select on Step 3
 *   - dept   : home dept ('default' | 'marketing' | 'sales' | 'support' | 'finance' | 'people')
 *   - askQ3  : if true, dept comes from Q3 instead, agent stays Luc
 */
const Q1_OPTIONS = [
  { id: 'founder',   label: 'Business owner or founder' },
  { id: 'dept-lead', label: 'Team or department lead' },
  { id: 'team-member', label: 'Team member' },
];

const Q2_OPTIONS = {
  founder: [
    { id: 'oversee',       label: 'Oversee the business', agent: 'stark', dept: 'default' },
    { id: 'drive-revenue', label: 'Drive revenue',         agent: 'sally', dept: 'default' },
    { id: 'run-campaigns', label: 'Run campaigns',         agent: 'marsi', dept: 'default' },
    { id: 'manage-team',   label: 'Manage the team',       agent: 'tali',  dept: 'default' },
    { id: 'track-finances',label: 'Track finances',        agent: 'finn',  dept: 'default' },
    { id: 'stay-organised',label: 'Stay organised',        agent: 'luc',   dept: 'default' },
  ],
  'dept-lead': [
    { id: 'close-deals',     label: 'Close deals',         agent: 'sally', dept: 'sales' },
    { id: 'run-campaigns',   label: 'Run campaigns',       agent: 'marsi', dept: 'marketing' },
    { id: 'support-customers',label:'Support customers',   agent: 'zira',  dept: 'support' },
    { id: 'manage-people',   label: 'Manage people',       agent: 'tali',  dept: 'people' },
    { id: 'track-finances',  label: 'Track finances',      agent: 'finn',  dept: 'finance' },
    { id: 'stay-organised',  label: 'Stay organised',      agent: 'luc',   dept: null, askQ3: true },
  ],
  'team-member': [
    { id: 'work-pipeline',   label: 'Work my pipeline',    agent: 'sally', dept: 'sales' },
    { id: 'execute-campaigns',label:'Execute campaigns',   agent: 'marsi', dept: 'marketing' },
    { id: 'handle-tickets',  label: 'Handle tickets',      agent: 'zira',  dept: 'support' },
    { id: 'manage-hiring',   label: 'Manage hiring',       agent: 'tali',  dept: 'people' },
    { id: 'track-expenses',  label: 'Track expenses',      agent: 'finn',  dept: 'finance' },
    { id: 'organise-work',   label: 'Organise my work',    agent: 'luc',   dept: null, askQ3: true },
  ],
};

const Q3_OPTIONS = [
  { id: 'sales',     label: 'Sales',     dept: 'sales' },
  { id: 'marketing', label: 'Marketing', dept: 'marketing' },
  { id: 'support',   label: 'Support',   dept: 'support' },
  { id: 'people',    label: 'People',    dept: 'people' },
  { id: 'finance',   label: 'Finance',   dept: 'finance' },
  { id: 'other',     label: 'Other',     dept: 'default' },
];

/* (Q1, Q2, Q3) → { agent, dept } resolver. Q3 only consulted when the
 * matched Q2 entry has `askQ3 === true`. */
export const resolveHowYouWork = (q1, q2, q3) => {
  if (!q1 || !q2) return { agent: 'luc', dept: 'default' };
  const q2Set = Q2_OPTIONS[q1] || [];
  const q2Entry = q2Set.find((o) => o.id === q2);
  if (!q2Entry) return { agent: 'luc', dept: 'default' };
  if (q2Entry.askQ3) {
    if (!q3) return { agent: q2Entry.agent, dept: 'default' };
    const q3Entry = Q3_OPTIONS.find((o) => o.id === q3);
    return { agent: q2Entry.agent, dept: q3Entry?.dept || 'default' };
  }
  return { agent: q2Entry.agent, dept: q2Entry.dept };
};

const ROLES = [
  // Founder / CEO tier — full-org orchestration (maps to old "founder")
  { id: 'founder-ceo', label: 'Founder / CEO', tier: 'founder' },
  { id: 'cofounder', label: 'Co-founder', tier: 'founder' },
  { id: 'coo', label: 'C-suite / COO', tier: 'founder' },
  { id: 'cmo', label: 'C-suite / CMO', tier: 'founder' },
  { id: 'cfo', label: 'C-suite / CFO', tier: 'founder' },
  { id: 'cro', label: 'C-suite / CRO', tier: 'founder' },
  { id: 'cto', label: 'C-suite / CTO', tier: 'founder' },
  { id: 'cpo', label: 'C-suite / CPO', tier: 'founder' },
  // Department head tier — your agents + your dept (maps to old "dept")
  { id: 'vp-sales', label: 'VP / Head of Sales', tier: 'dept' },
  { id: 'vp-marketing', label: 'VP / Head of Marketing', tier: 'dept' },
  { id: 'vp-cs', label: 'VP / Head of Customer Success', tier: 'dept' },
  { id: 'head-support', label: 'Head of Support', tier: 'dept' },
  { id: 'head-finance', label: 'Head of Finance', tier: 'dept' },
  { id: 'head-people', label: 'Head of People / HR', tier: 'dept' },
  { id: 'head-product', label: 'Head of Product', tier: 'dept' },
  { id: 'head-ops', label: 'Head of Operations', tier: 'dept' },
  { id: 'head-data', label: 'Head of Data / Analytics', tier: 'dept' },
  { id: 'head-design', label: 'Head of Design', tier: 'dept' },
  { id: 'head-eng', label: 'Head of Engineering', tier: 'dept' },
  // Individual contributor tier — personal agent + team context (maps to old "ic")
  { id: 'product-manager', label: 'Product Manager', tier: 'ic' },
  { id: 'marketing-manager', label: 'Marketing Manager', tier: 'ic' },
  { id: 'sales-manager', label: 'Sales Manager', tier: 'ic' },
  { id: 'ae', label: 'Account Executive', tier: 'ic' },
  { id: 'csm', label: 'Customer Success Manager', tier: 'ic' },
  { id: 'support-lead', label: 'Support Lead', tier: 'ic' },
  { id: 'finance-analyst', label: 'Finance / FP&A', tier: 'ic' },
  { id: 'recruiter', label: 'Recruiter / People Ops', tier: 'ic' },
  { id: 'designer', label: 'Designer', tier: 'ic' },
  { id: 'engineer', label: 'Engineer', tier: 'ic' },
  { id: 'analyst', label: 'Data Analyst', tier: 'ic' },
  { id: 'consultant', label: 'Consultant / Advisor', tier: 'ic' },
  { id: 'other', label: 'Other', tier: 'ic' },
];

const INDUSTRIES = [
  'Technology', 'Marketing & Media', 'Finance & Accounting', 'Consulting',
  'E-commerce', 'Real Estate', 'Healthcare', 'Legal', 'Education',
  'Hospitality', 'Construction', 'Manufacturing', 'Logistics', 'Other',
];

const AGENTS = [
  { id: 'luc', name: 'Luc', role: 'Chief of Staff', initial: 'L', thumb: '/Thumbnails/Luc.png', locked: true },
  { id: 'marsi', name: 'Marsi', role: 'Marketing', initial: 'M', thumb: '/Thumbnails/Marsi.png' },
  { id: 'sally', name: 'Sally', role: 'Sales', initial: 'S', thumb: '/Thumbnails/Sally.png' },
  { id: 'zira', name: 'Zira', role: 'Support', initial: 'Z', thumb: '/Thumbnails/Ziira.png' },
  { id: 'finn', name: 'Finn', role: 'Finance', initial: 'F', thumb: '/Thumbnails/Finn.png' },
  { id: 'tali', name: 'Tali', role: 'People', initial: 'T', thumb: '/Thumbnails/Tali.png' },
  { id: 'stark', name: 'Stark', role: 'Strategy', initial: 'St', thumb: '/Thumbnails/Stark.png' },
  { id: 'dora', name: 'Dora', role: 'Research', initial: 'D', thumb: '/Thumbnails/Dora.png' },
  { id: 'wright', name: 'Wright', role: 'Copywriting', initial: 'W', thumb: '/Thumbnails/Wright.png' },
  { id: 'buzz', name: 'Buzz', role: 'Social Media', initial: 'B', thumb: '/Thumbnails/Buzz.png' },
];

const DEPARTMENTS = [
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'sales', label: 'Sales', icon: TrendingUp },
  { id: 'support', label: 'Support', icon: Headphones },
  { id: 'finance', label: 'Finance', icon: DollarSign },
  { id: 'people', label: 'People', icon: Users },
];

const ALL_DEPT_IDS = DEPARTMENTS.map((d) => d.id);
const ALL_CORE_AGENTS = ['luc', 'marsi', 'sally', 'zira', 'finn', 'tali', 'stark', 'dora', 'wright', 'buzz'];

/* Department → relevant agents (matches V4 demo per-dept agent rosters).
   Luc is implicit (always pinned). */
const DEPT_AGENT_MAP = {
  marketing: ['marsi', 'wright', 'dora', 'buzz'],
  sales: ['sally', 'wright', 'dora', 'buzz'],
  support: ['zira', 'buzz'],
  finance: ['finn', 'dora', 'buzz'],
  people: ['tali', 'buzz'],
};

const deriveAgentsFromDepts = (depts) => {
  const set = new Set(['luc']); // Luc is always in.
  depts.forEach((d) => (DEPT_AGENT_MAP[d] || []).forEach((a) => set.add(a)));
  return Array.from(set);
};

/* Department-specific sidebar nav — mirrors V4 NAV_BY_ROLE so the preview
   visually transforms into the persona's view (Marketing-lead, Sales-lead, etc.).
   Each entry: { label, icon }. */
const NAV_DEPT = {
  marketing: [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'mkt-campaigns', label: 'Campaigns', icon: Target },
    { id: 'mkt-social', label: 'Social', icon: Share2 },
    { id: 'mkt-content', label: 'Content', icon: PenTool },
    { id: 'mkt-analytics', label: 'Analytics', icon: BarChart3 },
  ],
  sales: [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'sales-deals', label: 'Deals', icon: Target },
    { id: 'sales-pipeline', label: 'Pipeline', icon: Kanban },
    { id: 'sales-outreach', label: 'Outreach', icon: Mail },
    { id: 'sales-reports', label: 'Reports', icon: LineChart },
  ],
  support: [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'support-tickets', label: 'Tickets', icon: Ticket },
    { id: 'support-churn', label: 'Churn Risk', icon: ShieldAlert },
    { id: 'support-sentiment', label: 'Sentiment', icon: Heart },
  ],
  finance: [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'finance-cashflow', label: 'Cashflow', icon: Wallet },
    { id: 'finance-budgets', label: 'Budgets', icon: PieChart },
    { id: 'finance-invoices', label: 'Invoices', icon: FileText },
    { id: 'finance-reports', label: 'Reports', icon: ClipboardList },
  ],
  people: [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'people-team', label: 'Team', icon: Users },
    { id: 'people-hiring', label: 'Hiring', icon: UserPlus },
    { id: 'people-engagement', label: 'Engagement', icon: Smile },
    { id: 'people-org-health', label: 'Org Health', icon: Activity },
  ],
};

const NAV_CEO_TOP = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'org-pulse', label: 'Org Pulse', icon: Activity },
];

const NAV_DEPT_LABELS = [
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'sales', label: 'Sales', icon: TrendingUp },
  { id: 'support', label: 'Support', icon: Headphones },
  { id: 'finance', label: 'Finance', icon: DollarSign },
  { id: 'people', label: 'People', icon: Users },
];

const NAV_SYSTEM = [
  { id: 'history', label: 'History', icon: HistoryIcon },
  { id: 'agents', label: 'Agents', icon: Bot },
  { id: 'workflows', label: 'Workflows', icon: GitBranch },
  { id: 'knowledge-vault', label: 'Knowledge', icon: BookOpen },
  { id: 'integrations', label: 'Integrations', icon: Plug },
];

/* Role → default {depts, agents} for the activate step.
   Rule of thumb:
   - Founder tier: full org visibility — every dept, broad agent set
   - Dept tier: only the matching dept + agents relevant to that function
   - IC tier: dept they sit in + a small focused agent set */
const ROLE_DEFAULTS = {
  // Founder / C-suite tier
  'founder-ceo': { depts: ALL_DEPT_IDS, agents: ALL_CORE_AGENTS },
  'cofounder':   { depts: ALL_DEPT_IDS, agents: ALL_CORE_AGENTS },
  'coo':         { depts: ALL_DEPT_IDS, agents: ['luc', 'sally', 'finn', 'tali', 'stark', 'buzz'] },
  'cmo':         { depts: ['marketing'], agents: ['luc', 'marsi', 'wright', 'dora', 'buzz'] },
  'cfo':         { depts: ['finance'], agents: ['luc', 'finn', 'dora', 'buzz'] },
  'cro':         { depts: ['sales'], agents: ['luc', 'sally', 'wright', 'dora', 'buzz'] },
  'cto':         { depts: ['support'], agents: ['luc', 'stark', 'dora', 'buzz'] },
  'cpo':         { depts: ALL_DEPT_IDS, agents: ['luc', 'stark', 'dora', 'marsi', 'buzz'] },
  // Dept-head tier
  'vp-sales':       { depts: ['sales'], agents: ['luc', 'sally', 'wright', 'buzz'] },
  'vp-marketing':   { depts: ['marketing'], agents: ['luc', 'marsi', 'wright', 'dora', 'buzz'] },
  'vp-cs':          { depts: ['support'], agents: ['luc', 'zira', 'buzz'] },
  'head-support':   { depts: ['support'], agents: ['luc', 'zira', 'buzz'] },
  'head-finance':   { depts: ['finance'], agents: ['luc', 'finn', 'buzz'] },
  'head-people':    { depts: ['people'], agents: ['luc', 'tali', 'buzz'] },
  'head-product':   { depts: ['marketing'], agents: ['luc', 'stark', 'dora', 'buzz'] },
  'head-ops':       { depts: ALL_DEPT_IDS, agents: ['luc', 'stark', 'buzz'] },
  'head-data':      { depts: ALL_DEPT_IDS, agents: ['luc', 'dora', 'stark', 'buzz'] },
  'head-design':    { depts: ['marketing'], agents: ['luc', 'wright', 'marsi', 'buzz'] },
  'head-eng':       { depts: ['support'], agents: ['luc', 'stark', 'buzz'] },
  // IC tier
  'product-manager':   { depts: ['marketing'], agents: ['luc', 'dora', 'stark', 'buzz'] },
  'marketing-manager': { depts: ['marketing'], agents: ['luc', 'marsi', 'wright', 'buzz'] },
  'sales-manager':     { depts: ['sales'], agents: ['luc', 'sally', 'wright', 'buzz'] },
  'ae':                { depts: ['sales'], agents: ['luc', 'sally', 'wright', 'buzz'] },
  'csm':               { depts: ['support'], agents: ['luc', 'zira', 'buzz'] },
  'support-lead':      { depts: ['support'], agents: ['luc', 'zira', 'buzz'] },
  'finance-analyst':   { depts: ['finance'], agents: ['luc', 'finn', 'buzz'] },
  'recruiter':         { depts: ['people'], agents: ['luc', 'tali', 'buzz'] },
  'designer':          { depts: ['marketing'], agents: ['luc', 'wright', 'buzz'] },
  'engineer':          { depts: ['support'], agents: ['luc', 'stark', 'buzz'] },
  'analyst':           { depts: ALL_DEPT_IDS, agents: ['luc', 'dora', 'buzz'] },
  'consultant':        { depts: ALL_DEPT_IDS, agents: ['luc', 'stark', 'dora', 'buzz'] },
  'other':             { depts: [], agents: ['luc', 'buzz'] },
};

export const getRoleDefaults = (roleId) => {
  if (!roleId) return { depts: [], agents: ['luc'] };
  return ROLE_DEFAULTS[roleId] || { depts: [], agents: ['luc'] };
};

/* ─── Per-agent welcome content + Get Started workflows ─── */
const AGENT_WELCOME = {
  luc: {
    tagline: 'your chief of staff',
    intro: 'Implify gives you a team of AI specialists for every part of your business. I orchestrate them and keep you focused on what matters.',
    workflows: [
      { id: 'plan-day', title: 'Help me plan my day', sub: "Today's priorities" },
      { id: 'week-brief', title: 'Brief me on what changed this week', sub: 'Weekly summary' },
      { id: 'walk-priorities', title: 'Walk me through my priorities', sub: 'Focus session' },
      { id: 'decision-memo', title: 'Co-author a decision memo', sub: 'Strategic write-up' },
    ],
  },
  marsi: {
    tagline: 'your marketing lead',
    intro: 'Implify is your AI ops layer. I run marketing — campaigns, positioning, content — and pull in specialists when we need depth.',
    workflows: [
      { id: 'campaign-brief', title: 'Build a campaign brief from scratch', sub: 'Go-to-market plan' },
      { id: 'positioning-audit', title: 'Audit our positioning', sub: 'Messaging review' },
      { id: 'content-calendar', title: 'Plan a content calendar', sub: 'Editorial roadmap' },
      { id: 'launch-story', title: 'Workshop a launch story', sub: 'Narrative design' },
    ],
  },
  sally: {
    tagline: 'your sales operator',
    intro: 'Implify gives you AI teammates across every function. I drive sales — pipeline, follow-ups, outreach — and adapt as your motion evolves.',
    workflows: [
      { id: 'follow-up-playbook', title: 'Build a follow-up playbook', sub: 'Re-engage stalled deals' },
      { id: 'pipeline-walkthrough', title: 'Walk me through pipeline health', sub: 'Pipeline review' },
      { id: 'discovery-script', title: 'Draft a discovery script', sub: 'Call prep' },
      { id: 'pitch-prep', title: 'Prep for an upcoming pitch', sub: 'Buyer prep' },
    ],
  },
  zira: {
    tagline: 'your customer support lead',
    intro: 'Implify gives you AI specialists across the org. I own support — backlog, SOPs, churn signals — and bring in others when they help.',
    workflows: [
      { id: 'triage-playbook', title: 'Build a triage playbook', sub: 'Backlog framework' },
      { id: 'feedback-walkthrough', title: 'Walk me through customer feedback', sub: 'Voice of customer' },
      { id: 'common-sops', title: 'Draft SOPs for our common tickets', sub: 'Resolution playbooks' },
      { id: 'churn-patterns', title: 'Identify churn-risk patterns', sub: 'Retention scan' },
    ],
  },
  finn: {
    tagline: 'your finance partner',
    intro: 'Implify is a team of AI operators. I handle finance — cashflow, budgets, AR — and keep the numbers honest while you run the business.',
    workflows: [
      { id: 'cashflow-walkthrough', title: 'Walk me through our cashflow', sub: 'Liquidity review' },
      { id: 'budget-framework', title: 'Build a budget framework', sub: 'Quarterly planning' },
      { id: 'reminder-template', title: 'Build a payment-reminder template', sub: 'AR sequence' },
      { id: 'close-plan', title: 'Plan our financial close', sub: 'Month-end checklist' },
    ],
  },
  tali: {
    tagline: 'your people partner',
    intro: 'Implify gives you AI specialists for every function. I cover people — hiring, onboarding, engagement — and partner with the team to keep talent thriving.',
    workflows: [
      { id: 'hiring-brief', title: 'Draft a hiring brief', sub: 'Open role spec' },
      { id: 'onboarding-plan', title: 'Plan onboarding for a new hire', sub: 'First-30-days plan' },
      { id: 'engagement-check', title: 'Build an engagement check', sub: 'Pulse design' },
      { id: 'review-prep', title: 'Prep for a performance review', sub: 'Manager toolkit' },
    ],
  },
  stark: {
    tagline: 'your strategy partner',
    intro: 'Implify is your AI operating layer. I work on strategy — frameworks, market view, risks — and translate them into things your team can act on.',
    workflows: [
      { id: 'swot', title: 'Run a SWOT with me', sub: 'Strategic snapshot' },
      { id: 'gtm-outline', title: 'Outline a GTM strategy', sub: 'Launch frame' },
      { id: 'risk-map', title: 'Map our operational risks', sub: 'Risk register' },
      { id: 'roadmap-stress', title: 'Stress-test our roadmap', sub: 'Plan validation' },
    ],
  },
  dora: {
    tagline: 'your research analyst',
    intro: 'Implify gives you AI specialists across functions. I cover research — competitors, customers, market trends — so the rest of the team makes informed calls.',
    workflows: [
      { id: 'competitor-scan', title: 'Research our top 3 competitors', sub: 'Competitive scan' },
      { id: 'interview-synth', title: 'Synthesise customer interview notes', sub: 'Insight extract' },
      { id: 'trend-brief', title: 'Build a market trend brief', sub: 'Industry watch' },
      { id: 'opportunity-size', title: 'Help me size a new opportunity', sub: 'TAM/SAM' },
    ],
  },
  wright: {
    tagline: 'your in-house copywriter',
    intro: 'Implify gives you a full AI team. I handle copy — web, social, email, decks — in your voice, calibrated to the audience and the moment.',
    workflows: [
      { id: 'hero-rewrite', title: 'Rewrite our homepage hero', sub: 'Web copy' },
      { id: 'linkedin-post', title: 'Draft a LinkedIn thought-leadership post', sub: 'Social copy' },
      { id: 'landing-page', title: 'Co-write our landing page', sub: 'Conversion copy' },
      { id: 'email-sequence', title: 'Build an email sequence outline', sub: 'Lifecycle copy' },
    ],
  },
  buzz: {
    tagline: 'your generalist for everything else',
    intro: "Implify is a team of AI specialists. I'm the catch-all — anything that doesn't fit neatly into a function comes my way.",
    workflows: [
      { id: 'summarise', title: 'Summarise this for me', sub: 'Doc digest' },
      { id: 'checklist', title: 'Build a checklist', sub: 'Process scaffold' },
      { id: 'think-through', title: 'Help me think through something', sub: 'Open thinking partner' },
      { id: 'quick-research', title: 'Quick research dive', sub: 'Fast facts' },
    ],
  },
};

/* ─── Step Rail (horizontal, top) ─── */
export const StepRail = ({ activeIndex }) => (
  <header className="w-full flex items-center justify-between px-8 py-5 border-b border-white/[0.06] backdrop-blur-sm">
    <img src="/implify-logo-white.png" alt="Implify" className="h-7" />
    <nav className="flex items-center gap-2" data-tour="onboarding-rail">
      {RAIL_STEPS.map((step, i) => {
        const isActive = i === activeIndex;
        const isComplete = i < activeIndex;
        return (
          <React.Fragment key={step.id}>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${isActive ? 'bg-indigo-500/[0.08] border border-indigo-500/30' : 'border border-transparent'}`}
              data-testid={`onboarding-rail-step-${step.id}`}
            >
              <span className="w-3 flex-shrink-0 flex items-center justify-center">
                {isComplete ? (
                  <Check className="w-3 h-3 text-emerald-400" />
                ) : (
                  <span className={`text-[11px] font-mono ${isActive ? 'text-white/85' : 'text-white/40'}`}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                )}
              </span>
              <span className={`text-[12px] whitespace-nowrap ${isActive ? 'text-white/85 font-medium' : isComplete ? 'text-white/40' : 'text-white/55'}`}>
                {step.label}
              </span>
            </div>
            {i < RAIL_STEPS.length - 1 && (
              <span className={`block h-px w-8 ${isComplete ? 'bg-emerald-400/30' : 'bg-white/[0.06]'}`} />
            )}
          </React.Fragment>
        );
      })}
    </nav>
    {/* Spacer to balance the wordmark on the left so the stepper sits centered */}
    <span className="h-7 w-[88px] invisible" />
  </header>
);

/* ─── Reusable bits ─── */
const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40 mb-3">{children}</p>
);
const Title = ({ children }) => (
  <h1 className="text-[22px] font-semibold text-white/85 leading-tight">{children}</h1>
);
const Subtitle = ({ children }) => (
  <p className="text-[13px] text-white/60 mt-2 leading-relaxed">{children}</p>
);

const PrimaryBtn = ({ children, disabled, onClick, className = '', testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12px] font-semibold transition-colors bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:from-indigo-400 hover:to-purple-500 ${disabled ? 'opacity-40 pointer-events-none' : ''} ${className}`}
  >
    {children}
  </button>
);

const TertiaryBack = ({ onClick }) => (
  <button
    onClick={onClick}
    data-testid="onboarding-back"
    className="flex items-center gap-1 text-[12px] text-white/50 hover:text-white/80 transition-colors"
  >
    <ArrowLeft className="w-3.5 h-3.5" /> Back
  </button>
);

const AgentAvatar = ({ agent, initial, size = 28, thumb }) => {
  const src = thumb || agent?.thumb;
  const text = initial || agent?.initial;
  if (src) {
    return (
      <img
        src={src}
        alt={agent?.name || ''}
        className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white font-semibold flex-shrink-0"
      style={{ width: size, height: size, fontSize: size <= 24 ? 10 : 11 }}
    >
      {text}
    </span>
  );
};

/* ─── Screen 1: Welcome ─── */
export const WelcomeScreen = ({ name, setName, onNext }) => {
  const valid = name.trim().length > 0;
  return (
    <div data-testid="onboarding-screen-welcome" className="space-y-6">
      <div>
        <Eyebrow>Let's get started</Eyebrow>
        <Title>What should we call you?</Title>
        <Subtitle>This is the only thing we pre-fill. Everything else, you decide.</Subtitle>
      </div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && valid) onNext(); }}
        placeholder="Your first name"
        autoFocus
        className="w-full rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-[13px] text-white placeholder:text-white/30 outline-none focus:border-indigo-500/60 transition-colors"
        data-testid="onboarding-name-input"
      />
      <PrimaryBtn disabled={!valid} onClick={onNext} className="w-full" testId="onboarding-welcome-continue">
        Continue <ArrowRight className="w-3.5 h-3.5" />
      </PrimaryBtn>
    </div>
  );
};

/* ─── Screen 2: Role ─── */
export const RoleScreen = ({ value, setValue, onNext, onBack }) => {
  const [showAll, setShowAll] = useState(false);
  // If the currently selected role is in the hidden tail, auto-expand so the
  // user always sees their selection.
  const valueIndex = ROLES.findIndex((r) => r.id === value);
  const expanded = showAll || valueIndex >= 4;
  const visibleRoles = expanded ? ROLES : ROLES.slice(0, 4);
  return (
    <div data-testid="onboarding-screen-role" className="space-y-6">
      <div>
        <Eyebrow>Your Role</Eyebrow>
        <Title>How do you operate?</Title>
        <Subtitle>This shapes what you see and what your agents prioritise. Pick the closest match.</Subtitle>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="onboarding-role-grid">
        {visibleRoles.map((r) => {
          const sel = value === r.id;
          return (
            <button
              key={r.id}
              onClick={() => setValue(r.id)}
              data-testid={`onboarding-role-${r.id}`}
              className={`rounded-full px-3.5 py-1.5 text-[12px] transition-colors ${sel ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold border border-transparent' : 'border border-white/[0.08] text-white/70 hover:bg-white/[0.04]'}`}
            >
              {r.label}
            </button>
          );
        })}
        {!expanded && (
          <button
            onClick={() => setShowAll(true)}
            data-testid="onboarding-role-show-more"
            className="text-[12px] text-indigo-400 hover:text-indigo-300 transition-colors px-2 py-1.5"
          >
            Show more →
          </button>
        )}
        {showAll && (
          <button
            onClick={() => setShowAll(false)}
            data-testid="onboarding-role-show-less"
            className="text-[12px] text-white/50 hover:text-white/80 transition-colors px-2 py-1.5"
          >
            Show less
          </button>
        )}
      </div>
      <div className="flex items-center justify-between pt-2">
        <TertiaryBack onClick={onBack} />
        <PrimaryBtn disabled={!value} onClick={onNext} testId="onboarding-role-continue">
          Continue <ArrowRight className="w-3.5 h-3.5" />
        </PrimaryBtn>
      </div>
    </div>
  );
};

/* ─── Screen 3: Industry ─── */
export const IndustryScreen = ({ value, setValue, otherText, setOtherText, onNext, onBack }) => {
  const isOther = value === 'Other';
  return (
    <div data-testid="onboarding-screen-industry" className="space-y-6">
      <div>
        <Eyebrow>Your Industry</Eyebrow>
        <Title>What kind of business is this?</Title>
        <Subtitle>Agents use this to calibrate tone, benchmarks, and default workflows.</Subtitle>
      </div>
      <div className="flex flex-wrap gap-2">
        {INDUSTRIES.map((ind) => {
          const sel = value === ind;
          return (
            <button
              key={ind}
              onClick={() => setValue(ind)}
              data-testid={`onboarding-industry-${ind.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className={`rounded-full px-3.5 py-1.5 text-[12px] transition-colors ${sel ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold border border-transparent' : 'border border-white/[0.08] text-white/70 hover:bg-white/[0.04]'}`}
            >
              {ind}
            </button>
          );
        })}
      </div>
      {isOther && (
        <input
          type="text"
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder="Your industry"
          autoFocus
          className="w-full rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-2.5 text-[13px] text-white placeholder:text-white/30 outline-none focus:border-indigo-500/60 transition-colors"
          style={{ animation: 'onbFade 0.3s ease-out' }}
          data-testid="onboarding-industry-other-input"
        />
      )}
      <div className="flex items-center justify-between pt-2">
        <TertiaryBack onClick={onBack} />
        <PrimaryBtn
          disabled={!value || (isOther && !otherText.trim())}
          onClick={onNext}
          testId="onboarding-industry-continue"
        >
          Continue <ArrowRight className="w-3.5 h-3.5" />
        </PrimaryBtn>
      </div>
    </div>
  );
};

/* ─── Screen 2 (NEW): How you work — progressive Q1 → Q2 → Q3 ─── */
export const HowYouWorkScreen = ({ q1, setQ1, q2, setQ2, q3, setQ3, onNext, onBack }) => {
  // Reveal logic. Q2 only after Q1; Q3 only when current Q2 entry has askQ3.
  const q2Options = q1 ? (Q2_OPTIONS[q1] || []) : [];
  const q2Entry = q2 ? q2Options.find((o) => o.id === q2) : null;
  const showQ2 = !!q1;
  const showQ3 = !!(q2Entry && q2Entry.askQ3);

  // Reset downstream answers when an upstream answer flips. This keeps
  // routing consistent and prevents stale q3 leaking when the user changes Q1.
  const handleQ1 = (id) => {
    setQ1(id);
    if (id !== q1) { setQ2(null); setQ3(null); }
  };
  const handleQ2 = (id) => {
    setQ2(id);
    if (id !== q2) { setQ3(null); }
  };

  // Smooth-scroll Q3 into view on mount. Without this, Q3 reveals BELOW the
  // viewport and the items-center parent yanks the whole block upward to
  // re-centre — which is what made the Q3 reveal feel abrupt.
  const q3Ref = useRef(null);
  useEffect(() => {
    if (!showQ3 || !q3Ref.current) return;
    const t = setTimeout(() => {
      q3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    return () => clearTimeout(t);
  }, [showQ3]);

  // Continue is enabled when all *currently visible* questions are answered.
  const canContinue = !!q1 && !!q2 && (!showQ3 || !!q3);

  const Pill = ({ active, children, onClick, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full px-3.5 py-1.5 text-[12px] transition-colors ${
        active
          ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold border border-transparent'
          : 'border border-white/[0.08] text-white/70 hover:bg-white/[0.04]'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div data-testid="onboarding-screen-how-you-work">
      {/* Q1 */}
      <div className="space-y-3">
        <Eyebrow>How you work</Eyebrow>
        <Title>What best describes you?</Title>
        <Subtitle>Your workspace will be set up to match.</Subtitle>
        <div className="flex flex-wrap items-center gap-2 pt-4" data-testid="hyw-q1-options">
          {Q1_OPTIONS.map((o) => (
            <Pill
              key={o.id}
              active={q1 === o.id}
              onClick={() => handleQ1(o.id)}
              testId={`hyw-q1-${o.id}`}
            >
              {o.label}
            </Pill>
          ))}
        </div>
      </div>

      {/* Q2 — reveals after Q1, with a gentle slide-up + fade. Explicit
       * mt-16 keeps a safe gap above the heading even while the animation
       * is in-flight (the keyframe starts from translateY(20px) so the
       * margin acts as a buffer to prevent visual overlap with Q1 pills). */}
      {showQ2 && (
        <div
          key={`q2-${q1}`}
          className="space-y-3 mt-16"
          style={{ animation: 'onbReveal 0.9s cubic-bezier(0.16, 1, 0.3, 1) both' }}
        >
          <Title>What's your focus?</Title>
          <Subtitle>Pick one. You can always explore the rest.</Subtitle>
          <div className="flex flex-wrap items-center gap-2 pt-4" data-testid="hyw-q2-options">
            {q2Options.map((o) => (
              <Pill
                key={o.id}
                active={q2 === o.id}
                onClick={() => handleQ2(o.id)}
                testId={`hyw-q2-${o.id}`}
              >
                {o.label}
              </Pill>
            ))}
          </div>
        </div>
      )}

      {/* Q3 — reveals only when Q2 selection has askQ3. Uses scrollIntoView
       * (above) to glide into the centre of the viewport while the block
       * itself fades + slides up — eliminates the layout-shift jolt that
       * the items-center parent would otherwise create. */}
      {showQ3 && (
        <div
          ref={q3Ref}
          key={`q3-${q2}`}
          className="space-y-3 mt-16"
          style={{ animation: 'onbReveal 1.1s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both' }}
        >
          <Title>Which team are you on?</Title>
          <Subtitle>We'll set up your workspace around your team.</Subtitle>
          <div className="flex flex-wrap items-center gap-2 pt-4" data-testid="hyw-q3-options">
            {Q3_OPTIONS.map((o) => (
              <Pill
                key={o.id}
                active={q3 === o.id}
                onClick={() => setQ3(o.id)}
                testId={`hyw-q3-${o.id}`}
              >
                {o.label}
              </Pill>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-12">
        <TertiaryBack onClick={onBack} />
        <PrimaryBtn
          disabled={!canContinue}
          onClick={onNext}
          testId="onboarding-how-you-work-continue"
        >
          Continue <ArrowRight className="w-3.5 h-3.5" />
        </PrimaryBtn>
      </div>
    </div>
  );
};

/* ─── Sidebar preview — mock V4 sidebar that morphs by selected dept ─── */
const SidebarPreview = ({ depts, agents }) => {
  const allActive = depts.length === ALL_DEPT_IDS.length;
  const singleDept = !allActive && depts.length === 1 ? depts[0] : null;
  const selectedAgentObjs = AGENTS.filter((a) => agents.includes(a.id));

  return (
    <div
      className="w-full rounded-2xl border border-white/[0.08] bg-[#0d1b2a]/90 backdrop-blur-sm overflow-hidden shadow-2xl"
      data-testid="onboarding-sidebar-preview"
    >
      {/* Mock browser frame */}
      <div className="px-3.5 py-2.5 border-b border-white/[0.06] flex items-center gap-1.5 bg-white/[0.02]">
        <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
        <span className="ml-3 text-[10px] font-mono text-white/30">implify · live preview</span>
      </div>
      {/* Sidebar mock */}
      <div className="flex">
        <div className="w-[180px] flex-shrink-0 border-r border-white/[0.06] py-3 px-2 space-y-0.5 min-h-[420px]">
          <div className="px-3 pb-3">
            <img src="/implify-logo-white.png" alt="Implify" className="h-5 opacity-80" />
          </div>

          {singleDept ? (
            // Persona view — Marketing-lead / Sales-lead / etc.
            <>
              {NAV_DEPT[singleDept].map((it, i) => (
                <React.Fragment key={it.id}>
                  <PreviewNavItem icon={it.icon} label={it.label} active={i === 0} />
                  {i === 0 && <div className="my-2 border-t border-white/[0.06]" />}
                </React.Fragment>
              ))}
              <div className="my-2 border-t border-white/[0.06]" />
              {NAV_SYSTEM.map((it) => (
                <PreviewNavItem key={it.id} icon={it.icon} label={it.label} />
              ))}
            </>
          ) : (
            // CEO view — Org Pulse + every dept entry
            <>
              {NAV_CEO_TOP.map((it, i) => (
                <PreviewNavItem key={it.id} icon={it.icon} label={it.label} active={i === 0} />
              ))}
              <div className="my-2 border-t border-white/[0.06]" />
              <p className="px-3 pt-1 pb-1.5 text-[9px] font-semibold uppercase tracking-wider text-white/30">Departments</p>
              {NAV_DEPT_LABELS
                .filter((d) => allActive || depts.includes(d.id))
                .map((d) => (
                  <PreviewNavItem key={d.id} icon={d.icon} label={d.label} />
                ))}
              <div className="my-2 border-t border-white/[0.06]" />
              {NAV_SYSTEM.map((it) => (
                <PreviewNavItem key={it.id} icon={it.icon} label={it.label} />
              ))}
            </>
          )}
        </div>
        {/* Right: agents stack */}
        <div className="flex-1 px-4 py-3 min-h-[420px]">
          <p className="text-[9px] font-semibold uppercase tracking-wider text-white/30 mb-2.5">Your AI Team</p>
          <div className="space-y-1.5" data-testid="onboarding-preview-agents">
            {selectedAgentObjs.length === 0 ? (
              <p className="text-[11px] text-white/30">No agents selected yet</p>
            ) : (
              selectedAgentObjs.map((a) => (
                <div key={a.id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-white/[0.03] transition-colors">
                  <AgentAvatar agent={a} size={22} />
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-white/80 truncate">{a.name}</p>
                    <p className="text-[9px] text-white/35 truncate">{a.role}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewNavItem = ({ icon: Icon, label, active }) => (
  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] ${active ? 'bg-white/[0.06] text-white/85 font-medium' : 'text-white/55'}`}>
    <Icon className="w-3 h-3 flex-shrink-0" />
    <span className="truncate">{label}</span>
  </div>
);

/* ─── Screen 4: Activate Agents (2-column with live preview) ─── */
export const AgentGrid = ({
  selectedDepts, selectDept, selectAll,
  selectedAgents, toggleAgent,
  onNext, onBack,
}) => {
  const count = selectedAgents.length;
  const allActive = selectedDepts.length === ALL_DEPT_IDS.length;
  return (
    <div data-testid="onboarding-screen-agents" className="space-y-6">
      <div>
        <Eyebrow>Your AI Team</Eyebrow>
        <Title>Who do you want to onboard?</Title>
        <Subtitle>We've pre-selected based on your role. Tweak departments and agents to match how you operate.</Subtitle>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-6">
        {/* LEFT: dept + agent selectors */}
        <div className="space-y-5">
          {/* Departments — single-select; "All" is a shortcut */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40 mb-2.5">Departments</p>
            <div className="flex flex-wrap gap-2" data-testid="onboarding-dept-grid">
              <button
                onClick={selectAll}
                data-testid="onboarding-dept-all"
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] transition-colors ${allActive ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold border border-transparent' : 'border border-white/[0.08] text-white/70 hover:bg-white/[0.04]'}`}
              >
                All
              </button>
              {DEPARTMENTS.map((d) => {
                // When "All" is active, individual pills sit dim — only the
                // "All" pill carries the active treatment.
                const sel = !allActive && selectedDepts.includes(d.id);
                const Icon = d.icon;
                return (
                  <button
                    key={d.id}
                    onClick={() => selectDept(d.id)}
                    data-testid={`onboarding-dept-${d.id}`}
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] transition-colors ${sel ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold border border-transparent' : 'border border-white/[0.08] text-white/70 hover:bg-white/[0.04]'}`}
                  >
                    <Icon className="w-3 h-3" />
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Agents */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40 mb-2.5">Agents</p>
            <div className="grid grid-cols-2 gap-2.5">
              {[...AGENTS]
                .sort((a, b) => {
                  // Luc is always first (locked, pinned).
                  if (a.id === 'luc') return -1;
                  if (b.id === 'luc') return 1;
                  // Then selected agents, then unselected. Stable within each group
                  // by falling back to the original AGENTS order.
                  const aSel = selectedAgents.includes(a.id) ? 0 : 1;
                  const bSel = selectedAgents.includes(b.id) ? 0 : 1;
                  if (aSel !== bSel) return aSel - bSel;
                  return AGENTS.indexOf(a) - AGENTS.indexOf(b);
                })
                .map((a) => {
                const sel = selectedAgents.includes(a.id);
                const lockedCard = a.locked;
                return (
                  <button
                    key={a.id}
                    onClick={() => !lockedCard && toggleAgent(a.id)}
                    data-testid={`onboarding-agent-${a.id}`}
                    title={lockedCard ? 'Luc is always active' : undefined}
                    className={`relative flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors ${sel ? 'border-indigo-500/60 bg-indigo-500/[0.06]' : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'} ${lockedCard ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <AgentAvatar agent={a} size={26} />
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] font-semibold text-white/85 leading-tight truncate">{a.name}</p>
                      <p className="text-[10px] text-white/40 truncate">{a.role}</p>
                    </div>
                    <span className="absolute top-1.5 right-1.5">
                      {lockedCard ? (
                        <Lock className="w-3 h-3 text-white/20" />
                      ) : sel ? (
                        <Check className="w-3 h-3 text-emerald-400" style={{ animation: 'onbFade 0.25s ease-out' }} />
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3">
              <span className="rounded-full bg-white/[0.04] border border-white/[0.06] px-3 py-1 text-[11px] text-white/50" data-testid="onboarding-agent-counter">
                {count} agent{count === 1 ? '' : 's'} selected
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT: live sidebar preview */}
        <div className="space-y-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40">Your view</p>
          <SidebarPreview depts={selectedDepts} agents={selectedAgents} />
          <p className="text-[10px] text-white/35 leading-relaxed">
            This is a preview of your sidebar based on the departments and agents you've activated.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <TertiaryBack onClick={onBack} />
        <PrimaryBtn onClick={onNext} testId="onboarding-agents-continue">
          Build My Team <ArrowRight className="w-3.5 h-3.5" />
        </PrimaryBtn>
      </div>
    </div>
  );
};

/* ─── Screen 5: Get Started ─── */
export const GetStarted = ({ name, defaultAgentId = 'luc', onWorkflowSelected, onBack }) => {
  // Pre-selected agent comes from the (Q1, Q2, Q3) routing.
  const [activeAgentId, setActiveAgentId] = useState(defaultAgentId);
  const [agentBarOpen, setAgentBarOpen] = useState(false);
  const [message, setMessage] = useState('');

  // Keep activeAgent in sync if the resolved default changes (e.g. user
  // hits Back to Step 2 and changes Q2).
  useEffect(() => { setActiveAgentId(defaultAgentId); }, [defaultAgentId]);

  const agent = AGENTS.find((a) => a.id === activeAgentId) || AGENTS[0];
  const welcome = AGENT_WELCOME[activeAgentId] || AGENT_WELCOME.luc;

  // All 10 core agents in the strip — Luc + 6 dept primaries + 3 cross-
  // functional helpers (Dora research, Wright copy, Buzz social).
  const availableAgents = AGENTS.filter((a) => ALL_CORE_AGENTS.includes(a.id));

  return (
    <div data-testid="onboarding-screen-action" className="space-y-6">
      {/* Top agent strip */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3" data-testid="onboarding-top-agents">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-white/35 mb-2.5">Your agents</p>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {availableAgents.map((a) => {
            const sel = activeAgentId === a.id;
            return (
              <button
                key={a.id}
                onClick={() => setActiveAgentId(a.id)}
                data-testid={`onboarding-top-agent-${a.id}`}
                className={`flex items-center gap-2 rounded-full pl-1 pr-3 py-1 transition-colors flex-shrink-0 ${sel ? 'bg-indigo-500/[0.12] border border-indigo-500/40' : 'border border-white/[0.06] hover:bg-white/[0.04]'}`}
              >
                <AgentAvatar agent={a} size={22} />
                <span className={`text-[12px] ${sel ? 'text-white/90 font-semibold' : 'text-white/65'}`}>{a.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Welcome */}
      <div key={activeAgentId} style={{ animation: 'onbFade 0.25s ease-out' }} className="space-y-3">
        <div className="flex items-center gap-3">
          <AgentAvatar agent={agent} size={40} />
          <h2 className="text-[15px] font-semibold text-white/85 leading-tight">
            Hi {name || 'there'}, I'm {agent.name} — {welcome.tagline}.
          </h2>
        </div>
        <p className="text-[13px] leading-relaxed text-white/65">{welcome.intro}</p>
        <p className="text-[13px] text-white/55">Pick a workflow below to see me in action.</p>
      </div>

      {/* 2x2 workflow grid */}
      <div key={`workflows-${activeAgentId}`} className="grid grid-cols-2 gap-3" style={{ animation: 'onbFade 0.3s ease-out' }} data-testid="onboarding-workflow-grid">
        {welcome.workflows.map((wf) => (
          <button
            key={wf.id}
            onClick={() => onWorkflowSelected({ agentId: activeAgentId, agentName: agent.name, workflowId: wf.id, workflowTitle: wf.title })}
            data-testid={`onboarding-workflow-${wf.id}`}
            className="text-left rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3.5 transition-all hover:border-indigo-500/40 hover:bg-indigo-500/[0.04] hover:-translate-y-0.5"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/35 mb-1.5">{wf.sub}</p>
            <p className="text-[13px] font-semibold text-white/85 leading-tight">{wf.title}</p>
          </button>
        ))}
      </div>

      {/* Chat input pill — cosmetic */}
      <div className="rounded-2xl border border-white/[0.08] bg-[#0a1628]/80 backdrop-blur-sm flex flex-col" data-testid="onboarding-chat-input">
        <div className="px-4 pt-3 pb-2">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onFocus={() => setAgentBarOpen(false)}
            placeholder={`Message ${agent.name}…  (or pick a workflow above to see ${agent.name} in action)`}
            rows={1}
            className="w-full bg-transparent resize-none outline-none text-sm leading-5 text-white/90 placeholder:text-white/30"
            style={{ minHeight: '20px', maxHeight: '160px' }}
            data-testid="onboarding-chat-textarea"
          />
        </div>
        <div className="flex items-center justify-between px-3 pb-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setAgentBarOpen((o) => !o)}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${agentBarOpen ? 'bg-white/[0.08]' : 'hover:bg-white/[0.04]'}`}
              data-testid="onboarding-chat-agent-switcher"
            >
              <AgentAvatar agent={agent} size={20} />
              <span className="text-xs font-medium text-white/80">{agent.name}</span>
            </button>
            <button className="p-1.5 rounded-lg transition-colors hover:bg-white/[0.04]" data-testid="onboarding-chat-attach-btn">
              <Paperclip className="w-3.5 h-3.5 text-white/55" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors hover:bg-white/[0.04]" data-testid="onboarding-chat-voice-btn">
              <Mic className="w-4 h-4 text-white/55" />
            </button>
            <button
              className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-white/[0.06]'}`}
              data-testid="onboarding-chat-send-btn"
            >
              <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : 'text-white/55'}`} />
            </button>
          </div>
        </div>
        {agentBarOpen && (
          <div className="border-t border-white/[0.06] px-3 py-2 flex items-center gap-1.5 overflow-x-auto" data-testid="onboarding-chat-agent-bar">
            {availableAgents.map((a) => {
              const sel = activeAgentId === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => { setActiveAgentId(a.id); setAgentBarOpen(false); }}
                  data-testid={`onboarding-chat-agent-${a.id}`}
                  className={`flex items-center gap-1.5 rounded-lg px-2 py-1 transition-colors flex-shrink-0 ${sel ? 'bg-indigo-500/[0.15]' : 'hover:bg-white/[0.04]'}`}
                >
                  <AgentAvatar agent={a} size={18} />
                  <span className={`text-[11px] ${sel ? 'text-white/90 font-semibold' : 'text-white/65'}`}>{a.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2">
        <TertiaryBack onClick={onBack} />
      </div>
    </div>
  );
};


/* ─── Pill (used in modal) ─── */
const Pill = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`rounded-full px-3 py-1 text-[11px] transition-colors ${active ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold border border-transparent' : 'border border-white/[0.08] text-white/70 hover:bg-white/[0.04]'}`}
  >
    {children}
  </button>
);

const SmallPrimary = ({ children, onClick, disabled, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-colors bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:from-indigo-400 hover:to-purple-500 ${disabled ? 'opacity-40 pointer-events-none' : ''}`}
  >
    {children}
  </button>
);

const TEAM_SIZES = ['1–10', '11–50', '51–200', '200+'];
const FOCUS_OPTIONS = ['Revenue growth', 'Operational efficiency', 'Team scaling', 'Customer retention', 'Cost reduction'];
const TOOLS = [
  'HubSpot', 'Notion', 'Slack', 'Gmail', 'Outlook', 'Meta Ads', 'LinkedIn',
  'WhatsApp', 'ClickUp', 'Xero', 'QuickBooks', 'Zoom', 'MS Teams', 'Salesforce',
];
const INVITE_ROLES = ['Founder', 'C-suite', 'Manager', 'Team member'];

/* SetupModal designation pills — dynamic based on Q1 (how-you-work tier).
 * Founders see C-suite/founder-tier titles; dept leads see manager titles;
 * team members see IC titles. Each list ends with Other → text input. */
const DESIGNATIONS_BY_TIER = {
  founder: [
    'Founder', 'Co-founder', 'CEO', 'COO', 'CFO', 'CMO', 'CTO', 'CRO', 'CPO',
  ],
  'dept-lead': [
    'Head of Sales', 'Sales Manager', 'Head of Marketing', 'Marketing Manager',
    'Head of Support', 'Support Lead', 'Head of People', 'HR Manager',
    'Head of Finance', 'Finance Manager', 'Project Manager', 'Operations Manager',
    'Engineering Manager', 'Product Manager',
  ],
  'team-member': [
    'Account Executive', 'SDR / BDR', 'Sales Rep', 'Customer Success Manager',
    'Marketing Specialist', 'Content Writer', 'Copywriter', 'Designer',
    'Social Media Manager', 'Performance Marketer', 'Support Agent',
    'HR Business Partner', 'Recruiter', 'Accountant', 'Bookkeeper',
    'Software Engineer', 'Product Designer', 'Data Analyst',
  ],
  // Fallback for any unmapped tier — generic list.
  default: ['Founder / CEO', 'C-suite', 'Department head', 'Team member'],
};
const getDesignations = (tier) => [...(DESIGNATIONS_BY_TIER[tier] || DESIGNATIONS_BY_TIER.default), 'Other'];

/* SetupModal industry pills — exhaustive list (matches the depth of the
 * Role lists). "Other" opens a free-text input. */
const SETUP_INDUSTRIES = [
  'Technology / SaaS', 'E-commerce / Retail', 'Marketing & Advertising',
  'Financial Services', 'Healthcare', 'Education / EdTech',
  'Real Estate', 'Manufacturing', 'Logistics & Supply Chain',
  'Hospitality / Travel', 'Media & Entertainment', 'Consulting',
  'Legal Services', 'Construction', 'Non-profit', 'Other',
];

/* ─── Setup Modal — multi-step wizard (6 steps, one input at a time) ─── */
export const SetupModal = ({ open, onClose, onFinish, tier }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [completed, setCompleted] = useState({});
  // Item state
  const [companyName, setCompanyName] = useState('');
  const [teamSize, setTeamSize] = useState(null);
  // Designation + industry — moved here from the legacy onboarding flow.
  // Both follow the "few pills + Other → text input" pattern.
  const [designation, setDesignation] = useState(null);
  const [designationOther, setDesignationOther] = useState('');
  const [industry, setIndustry] = useState(null);
  const [industryOther, setIndustryOther] = useState('');
  const [tools, setTools] = useState([]);
  const [invites, setInvites] = useState([{ email: '', role: 'Founder' }]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [file, setFile] = useState(null);

  if (!open) return null;

  const completeCount = Object.values(completed).filter(Boolean).length;

  const markDone = (id) => setCompleted((p) => ({ ...p, [id]: true }));
  const toggleTool = (t) =>
    setTools((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const addInviteRow = () => setInvites((cur) => [...cur, { email: '', role: 'Founder' }]);
  const updateInvite = (i, k, v) => setInvites((cur) => cur.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const sendInvites = () => {
    const valid = invites.filter((r) => r.email.trim());
    if (!valid.length) return;
    setPendingInvites((cur) => [...cur, ...valid]);
    setInvites([{ email: '', role: 'Founder' }]);
    markDone('invite');
  };

  const ITEMS = [
    {
      id: 'company',
      canSave: () => companyName.trim() && teamSize,
      render: () => (
        <div className="mt-1 space-y-5" style={{ animation: 'onbFade 0.3s ease-out' }}>
          <div>
            <label className="text-[12px] font-medium text-white/70 block mb-2">Company name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Implify"
              className="w-full rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2 text-[12px] text-white placeholder:text-white/30 outline-none focus:border-indigo-500/60 transition-colors"
              data-testid="setup-company-name"
            />
          </div>
          <div>
            <label className="text-[12px] font-medium text-white/70 block mb-2">How big is your team?</label>
            <div className="flex flex-wrap gap-2">
              {TEAM_SIZES.map((s) => (
                <Pill
                  key={s}
                  active={teamSize === s}
                  onClick={() => setTeamSize(s)}
                  testId={`setup-team-${s.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  {s}
                </Pill>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'designation',
      canSave: () => !!designation && (designation !== 'Other' || designationOther.trim()),
      render: () => {
        const designationOptions = getDesignations(tier);
        return (
        <div className="mt-1 space-y-3" style={{ animation: 'onbFade 0.3s ease-out' }}>
          <label className="text-[13px] font-medium text-white/85 block">What's your role?</label>
          <div className="flex flex-wrap gap-2">
            {designationOptions.map((d) => (
              <Pill
                key={d}
                active={designation === d}
                onClick={() => setDesignation(d)}
                testId={`setup-designation-${d.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {d}
              </Pill>
            ))}
          </div>
          {designation === 'Other' && (
            <input
              type="text"
              value={designationOther}
              onChange={(e) => setDesignationOther(e.target.value)}
              placeholder="Your role"
              className="w-full rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5 mt-6 text-[12px] text-white placeholder:text-white/30 outline-none focus:border-indigo-500/60 transition-colors"
              style={{ animation: 'onbFade 0.3s ease-out' }}
              data-testid="setup-designation-other-input"
            />
          )}
        </div>
        );
      },
    },
    {
      id: 'industry',
      canSave: () => !!industry && (industry !== 'Other' || industryOther.trim()),
      render: () => (
        <div className="mt-1 space-y-5" style={{ animation: 'onbFade 0.3s ease-out' }}>
          <label className="text-[13px] font-medium text-white/85 block">What kind of business is this?</label>
          <div className="flex flex-wrap gap-2">
            {SETUP_INDUSTRIES.map((ind) => (
              <Pill
                key={ind}
                active={industry === ind}
                onClick={() => setIndustry(ind)}
                testId={`setup-industry-${ind.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {ind}
              </Pill>
            ))}
          </div>
          {industry === 'Other' && (
            <input
              type="text"
              value={industryOther}
              onChange={(e) => setIndustryOther(e.target.value)}
              placeholder="Your industry"
              className="w-full rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5 mt-6 text-[12px] text-white placeholder:text-white/30 outline-none focus:border-indigo-500/60 transition-colors"
              style={{ animation: 'onbFade 0.3s ease-out' }}
              data-testid="setup-industry-other-input"
            />
          )}
        </div>
      ),
    },
    {
      id: 'tools',
      canSave: () => tools.length > 0,
      render: () => (
        <div className="mt-1" style={{ animation: 'onbFade 0.3s ease-out' }}>
          <label className="text-[13px] font-medium text-white/85 block mb-3">Which tools does your team use today?</label>
          <div className="flex flex-wrap gap-2">
            {TOOLS.map((t) => (
              <Pill key={t} active={tools.includes(t)} onClick={() => toggleTool(t)} testId={`setup-tool-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                {t}
              </Pill>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'invite',
      canSave: () => invites.some((r) => r.email.trim()) || pendingInvites.length > 0,
      render: () => (
        <div className="mt-1 space-y-5" style={{ animation: 'onbFade 0.3s ease-out' }}>
          <label className="text-[13px] font-medium text-white/85 block">Invite your team — each gets their own seat and agents</label>
          <div className="space-y-3">
            {invites.map((row, i) => (
              <div key={i} className="flex gap-3">
                <input
                  type="email" value={row.email} onChange={(e) => updateInvite(i, 'email', e.target.value)} placeholder="email@company.com"
                  className="flex-1 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5 text-[12px] text-white placeholder:text-white/30 outline-none focus:border-indigo-500/60 transition-colors"
                  data-testid={`setup-invite-${i}-email`}
                />
                <select
                  value={row.role} onChange={(e) => updateInvite(i, 'role', e.target.value)}
                  className="w-36 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5 text-[12px] text-white outline-none focus:border-indigo-500/60 transition-colors"
                  data-testid={`setup-invite-${i}-role`}
                >
                  {INVITE_ROLES.map((r) => <option key={r} value={r} className="bg-[#0d1b2a]">{r}</option>)}
                </select>
              </div>
            ))}
          </div>
          <button
            onClick={addInviteRow}
            className="text-[12px] text-indigo-400 hover:text-indigo-300 transition-colors"
            data-testid="setup-invite-add-row"
          >
            + Add another
          </button>
          {pendingInvites.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {pendingInvites.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 px-2 py-0.5 text-[11px] text-emerald-300">
                  <Check className="w-3 h-3" /> {p.email}
                  <button
                    onClick={() => setPendingInvites((cur) => cur.filter((_, idx) => idx !== i))}
                    className="text-emerald-300/60 hover:text-emerald-300"
                    data-testid={`setup-invite-pending-${i}-remove`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'context',
      canSave: () => !!file,
      render: () => (
        <div className="mt-1 space-y-3" style={{ animation: 'onbFade 0.3s ease-out' }}>
          <label className="text-[13px] font-medium text-white/85 block">Add documents that teach agents about your business</label>
          <label className="block rounded-xl border border-dashed border-white/[0.1] bg-white/[0.02] p-5 text-center cursor-pointer hover:border-indigo-500/40 transition-colors" data-testid="setup-upload-zone">
            <Upload className="w-5 h-5 text-white/30 mx-auto mb-1.5" />
            <p className="text-[12px] text-white/40">{file ? file.name : 'Drop a file or browse'}</p>
            <p className="text-[10px] text-white/30 mt-1">Accepted: PDF, DOCX, XLSX</p>
            <input
              type="file" accept=".pdf,.docx,.xlsx" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="setup-upload-input"
            />
          </label>
          {file && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] px-2 py-0.5 text-[11px] text-white/70">
              {file.name}
              <button onClick={() => setFile(null)} className="text-white/30 hover:text-white/70" data-testid="setup-upload-remove">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      ),
    },
  ];

  const totalSteps = ITEMS.length;
  const currentItem = ITEMS[stepIdx];
  const isLastStep = stepIdx === totalSteps - 1;
  const isFirstStep = stepIdx === 0;
  const allDone = completeCount === totalSteps;
  const goNext = () => setStepIdx((i) => Math.min(i + 1, totalSteps - 1));
  const goPrev = () => setStepIdx((i) => Math.max(i - 1, 0));

  const handleSave = () => {
    if (!currentItem.canSave()) return;
    // Step-specific side-effects (e.g. invite step bumps invites → pending list).
    if (currentItem.id === 'invite') sendInvites();
    markDone(currentItem.id);
    if (isLastStep) {
      // Finish if everything is done; otherwise just close (synthetic task
      // appears in home for any remaining steps).
      if (Object.values({ ...completed, [currentItem.id]: true }).filter(Boolean).length === totalSteps) {
        onFinish();
      } else {
        onClose();
      }
    } else {
      goNext();
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto" data-testid="setup-modal">
      <div
        className="relative w-full max-w-lg rounded-2xl backdrop-blur-xl bg-[#0f1c2e]/85 shadow-2xl ring-1 ring-indigo-500/20 overflow-hidden"
        style={{ animation: 'onbFade 0.35s ease-out' }}
      >
        <div className="h-px w-full bg-gradient-to-r from-indigo-400/0 via-indigo-400/80 to-purple-500/0" />
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
          data-testid="setup-modal-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="p-5">
          <Eyebrow>Setup checklist</Eyebrow>
          <h3 className="text-[15px] font-semibold text-white">Make Implify smarter</h3>
          <p className="text-[12px] text-white/50 mt-1">Takes 2 minutes. Skip anything and come back later.</p>

          {/* Horizontal step bar — 5 segments, no labels. Completed steps show
           * indigo→purple gradient; the current step shows a soft white fill. */}
          <div className="mt-5 flex items-center gap-1.5" data-testid="setup-step-bar">
            {ITEMS.map((it, i) => {
              const done = !!completed[it.id];
              const isCurrent = i === stepIdx;
              return (
                <div
                  key={it.id}
                  className={`flex-1 h-1 rounded-full transition-colors duration-300 ${
                    done
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600'
                      : isCurrent
                        ? 'bg-white/35'
                        : 'bg-white/[0.08]'
                  }`}
                />
              );
            })}
          </div>

          {/* Current step content — renders only the input(s); no header
           * title, no description, no completion circle (per user spec). */}
          <div
            key={currentItem.id}
            className="mt-6"
            data-testid={`setup-step-${currentItem.id}`}
            style={{ animation: 'onbFade 0.3s ease-out' }}
          >
            {currentItem.render()}
          </div>

          {/* Footer — [← Back] ............. [Skip] [Save / Finish] */}
          <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-center justify-between">
            <button
              onClick={goPrev}
              disabled={isFirstStep}
              className={`text-[12px] transition-colors ${isFirstStep ? 'text-white/20 cursor-not-allowed' : 'text-white/55 hover:text-white/80'}`}
              data-testid="setup-prev"
            >
              ← Back
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={isLastStep ? onClose : goNext}
                className="px-3 py-1.5 rounded-lg text-[12px] font-medium border border-white/[0.1] text-white/70 hover:bg-white/[0.06] transition-colors"
                data-testid="setup-skip"
              >
                {isLastStep ? 'Remind me later' : 'Skip'}
              </button>
              <PrimaryBtn
                onClick={handleSave}
                disabled={!currentItem.canSave()}
                testId="setup-save"
              >
                {isLastStep ? (allDone || currentItem.canSave() ? <>Finish <ArrowRight className="w-3.5 h-3.5" /></> : <>Save <ArrowRight className="w-3.5 h-3.5" /></>) : <>Save <ArrowRight className="w-3.5 h-3.5" /></>}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ─── Main flow ─── */
const OnboardingFlow = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const [name, setName] = useState('');
  // Step 2 — How you work (3 progressive questions)
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [q3, setQ3] = useState(null);

  const [transitioning, setTransitioning] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  // Result of the workflow simulation that ran on Step 3 — drives the
  // Onboarded Home agent + workflow to play.
  const [simResult, setSimResult] = useState(null);

  const next = () => setStepIndex((i) => {
    const nextIdx = Math.min(RAIL_STEPS.length - 1, i + 1);
    if (nextIdx !== i) trackEvent('onboarding_step_complete', { step: RAIL_STEPS[i].id, next_step: RAIL_STEPS[nextIdx].id });
    return nextIdx;
  });
  const back = () => setStepIndex((i) => Math.max(0, i - 1));

  // Resolve mapped agent + dept from (Q1, Q2, Q3). Used as the default
  // selected agent on Step 3 and as the home dept after a workflow runs.
  const { agent: mappedAgent, dept: mappedDept } = resolveHowYouWork(q1, q2, q3);

  // Workflow click on Step 3 — store selection and transition into the
  // Onboarded Home, where the conversation will run inside the chat panel.
  const handleWorkflowSelected = (sel) => {
    trackEvent('onboarding_workflow_chosen', {
      workflow_id: sel?.workflowId,
      workflow_title: sel?.workflowTitle,
      agent_id: sel?.agentId,
      tier: q1,
    });
    trackPageView({ path: '/v4/onboarded-home', title: 'Implify · Onboarded Home' });
    setSimResult(sel); // { agentId, agentName, workflowId, workflowTitle }
    setTransitioning(true);
    setTimeout(() => setShowDashboard(true), 700);
  };

  // The simulation no longer triggers a setup modal — the home is itself
  // the post-onboarding surface, and the chat → briefing flow is the moment.
  const handleSimComplete = () => { /* no-op */ };

  const screen = (() => {
    switch (RAIL_STEPS[stepIndex].id) {
      case 'welcome':
        return <WelcomeScreen name={name} setName={setName} onNext={next} />;
      case 'how-you-work':
        return (
          <HowYouWorkScreen
            q1={q1} setQ1={setQ1}
            q2={q2} setQ2={setQ2}
            q3={q3} setQ3={setQ3}
            onNext={next} onBack={back}
          />
        );
      case 'action':
        return (
          <GetStarted
            name={name}
            defaultAgentId={mappedAgent}
            onWorkflowSelected={handleWorkflowSelected}
            onBack={back}
          />
        );
      default:
        return null;
    }
  })();

  const currentStepId = RAIL_STEPS[stepIndex].id;
  const isMediumStep = currentStepId === 'action';

  if (showDashboard) {
    return (
      <>
        <V4OnboardedHome
          name={name}
          agentId={simResult?.agentId || mappedAgent}
          agentName={simResult?.agentName || (AGENTS.find((a) => a.id === mappedAgent)?.name) || 'Luc'}
          department={mappedDept}
          tier={q1}
          workflowId={simResult?.workflowId}
          workflowTitle={simResult?.workflowTitle}
          onExploreFullDemo={() => { window.location.href = '/'; }}
          onSimulationComplete={handleSimComplete}
        />
        <style>{onbKeyframes}</style>
      </>
    );
  }

  return (
    <div className="fixed inset-0 aurora-gradient flex flex-col" data-testid="onboarding-root">
      <style>{onbKeyframes}</style>
      <StepRail activeIndex={stepIndex} />
      <main className="flex-1 flex items-center justify-center px-8 py-8 overflow-y-auto">
        <div
          key={stepIndex}
          className={`w-full ${isMediumStep ? 'max-w-[60vw]' : 'max-w-lg'} ${transitioning ? 'opacity-0' : 'opacity-100'}`}
          style={{ animation: transitioning ? 'onbFadeOut 0.4s ease-out forwards' : 'onbFade 0.35s ease-out' }}
        >
          {screen}
        </div>
      </main>
    </div>
  );
};

const onbKeyframes = `
  @keyframes onbFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes onbFadeOut { from { opacity: 1; } to { opacity: 0; } }
  @keyframes onbDot { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
  @keyframes onbReveal {
    from { opacity: 0; transform: translateY(20px); filter: blur(3px); }
    to   { opacity: 1; transform: translateY(0);    filter: blur(0); }
  }
`;

export default OnboardingFlow;
